import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ShieldCheck, Pencil, AlertTriangle } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { api } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { expiryStatus } from '@/lib/license';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';

interface Props {
  deviceId: number | undefined;
  clientId: string;
  expires_at: string | null | undefined;
  isSuperadmin: boolean;
}

export function LicenseStrip({ deviceId, clientId, expires_at, isSuperadmin }: Props) {
  const lic = expiryStatus(expires_at);
  const expired = lic.kind === 'expired';
  const [editing, setEditing] = useState(false);
  const toast = useToast();
  const idMissing = deviceId === undefined || deviceId === null || Number.isNaN(deviceId);

  const setExpiry = useMutation({
    mutationFn: (date: string | null) => {
      if (idMissing) {
        throw new Error('Device id missing — refresh the page or update the server.');
      }
      return api(`/api/devices/${deviceId}/expiry`, { method: 'POST', body: { expires_at: date } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['device-status', clientId] });
      setEditing(false);
      toast.ok('License updated');
    },
    onError: (e: Error) => toast.error(e.message || 'License update failed'),
  });

  const tone = lic.kind === 'expired' ? 'bad' : lic.kind === 'warn' ? 'warn' : lic.kind === 'active' ? 'ok' : 'neutral';

  return (
    <div className={cn(
      'flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border text-sm',
      expired ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200',
    )}>
      <div className="flex items-center gap-2 flex-wrap">
        {expired ? <AlertTriangle className="w-4 h-4 text-bad" /> : <ShieldCheck className="w-4 h-4 text-slate-500" />}
        <span className="font-semibold text-ink">License</span>
        <Badge tone={tone}>{lic.text}</Badge>
        {expires_at && <span className="text-xs text-slate-500 font-mono">({expires_at})</span>}
      </div>
      {isSuperadmin && (
        <Popover.Root open={editing} onOpenChange={setEditing}>
          <Popover.Trigger asChild>
            <Button
              size="sm" variant="ghost" className="!h-8 !px-2"
              disabled={idMissing}
              title={idMissing ? 'Server not yet redeployed — restart Node to enable license editing' : undefined}
            >
              <Pencil className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Edit</span>
            </Button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              align="end" sideOffset={8}
              className="z-50 w-72 bg-white rounded-xl border border-slate-200 shadow-lg p-4 space-y-3"
            >
              <div className="text-sm font-bold">Set expiry date</div>
              <Input
                type="date"
                defaultValue={expires_at ? expires_at.slice(0, 10) : ''}
                id="exp-input"
              />
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  size="sm"
                  loading={setExpiry.isPending}
                  onClick={() => {
                    const v = (document.getElementById('exp-input') as HTMLInputElement).value;
                    if (!v) { toast.warn('Pick a date first'); return; }
                    setExpiry.mutate(v);
                  }}
                >
                  Save
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (confirm('Remove expiry — license will not lapse?')) setExpiry.mutate(null);
                  }}
                >
                  Clear
                </Button>
              </div>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      )}
    </div>
  );
}
